// src/pages/login.tsx
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { UserCheck } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

const loginSchema = z.object({
  username: z
    .string()
    .min(3, "Username must be at least 3 characters")
    .max(20, "Username must be less than 20 characters"),
  adminPassword: z.string().optional(),
});

type LoginForm = z.infer<typeof loginSchema>;

const Login = () => {
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const { login, isAuthLoading } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const ref = searchParams.get("ref");
    if (ref) {
      setReferralCode(ref);
    }
  }, []);

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: "", adminPassword: "" },
  });

  const watchedUsername = form.watch("username");

  useEffect(() => {
    // Basic debounce
    const handler = setTimeout(async () => {
      if (watchedUsername && watchedUsername.length >= 3) {
        try {
          const res = await apiRequest("GET", `/api/auth/check-admin/${watchedUsername}`);
          if (res.ok) {
            const { isAdmin } = await res.json();
            setShowAdminPassword(isAdmin);
          } else {
            setShowAdminPassword(false);
          }
        } catch (error) {
          setShowAdminPassword(false); // On error, assume not admin
        }
      } else {
        setShowAdminPassword(false);
      }
    }, 500); // Wait 500ms after user stops typing

    return () => {
      clearTimeout(handler);
    };
  }, [watchedUsername]);

  const onSubmit = async (data: LoginForm) => {
    try {
      // The new login function handles everything and returns the user.
      await login(data.username, referralCode ?? undefined, data.adminPassword);

      toast({
        title: "Login Successful",
        description: "Welcome to BloxBoost!",
      });

      // Navigate to the earn page after a successful login.
      setLocation("/earn");

    } catch (error) {
      toast({
        title: "Login Failed",
        description: error instanceof Error ? error.message : "An unknown error occurred.",
        variant: "destructive",
      });
    }
  };

  const disableAll = form.formState.isSubmitting || isAuthLoading;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Card className="glass-effect border-border">
          <CardHeader className="text-center">
            <motion.div
              className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <UserCheck className="w-8 h-8 text-primary" />
            </motion.div>
            <CardTitle className="text-2xl font-bold">
              Welcome Back
            </CardTitle>
            <p className="text-muted-foreground">
              Login with your Roblox username to continue
            </p>
          </CardHeader>

          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" aria-live="polite">
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Roblox Username</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your Roblox username" {...field} disabled={disableAll} data-testid="input-username" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <motion.div
                  initial={false}
                  animate={{ height: showAdminPassword ? 'auto' : 0, opacity: showAdminPassword ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <FormField
                    control={form.control}
                    name="adminPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Admin Password</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="Enter admin password"
                            {...field}
                            disabled={disableAll}
                            data-testid="input-admin-password"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </motion.div>

                <Button type="submit" className="w-full glow-effect" disabled={disableAll} data-testid="button-submit">
                  {disableAll ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground" />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    "Login"
                  )}
                </Button>
              </form>
            </Form>

            <div className="mt-6 pt-6 border-t border-border text-center">
              <p className="text-sm text-muted-foreground">
                New to BloxBoost?{" "}
                <span className="text-primary hover:underline cursor-pointer" onClick={() => setLocation("/")}>
                  Learn how it works
                </span>
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Login;
